/* eslint-disable indent */
import { useState, useRef } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export const useEditableTag = (canvasSize) => {
	const canvasRef = useRef(null);
	const [elements, setElements] = useState([]);
	const [initialElements, setInitialElements] = useState([]);
	const [selectedElement, setSelectedElement] = useState(null);
	const [isDragging, setIsDragging] = useState(false);
	const [isResizing, setIsResizing] = useState(false);
	const [resizeHandle, setResizeHandle] = useState(null);
	const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(3);

	const handleMouseDown = (e, element, handle = null) => {
		if (!element) return;
		e.stopPropagation();
		setSelectedElement(element.id);

		const rect = canvasRef.current.getBoundingClientRect();
		const x = (e.clientX - rect.left) / zoom;
		const y = (e.clientY - rect.top) / zoom;

		if (handle) {
			setIsResizing(true);
			setResizeHandle(handle);
		} else {
			setIsDragging(true);
		}

		setDragStart({
			x: x - element.x,
			y: y - element.y,
			elementWidth: element.width,
			elementHeight: element.height,
			elementX: element.x,
			elementY: element.y
		});
	};

	const handleElementClick = (e, element) => {
		e.stopPropagation();
		setSelectedElement(element.id);
	};

	const handleMouseMove = (e) => {
		if (!selectedElement) return;

		const rect = canvasRef.current.getBoundingClientRect();
		const x = (e.clientX - rect.left) / zoom;
		const y = (e.clientY - rect.top) / zoom;

		setElements(prevElements =>
			prevElements.map(el => {
				if (el.id === selectedElement) {
					if (isDragging) {
						return {
							...el,
							x: Math.max(0, Math.min(x - dragStart.x, canvasSize.width - el.width)),
							y: Math.max(0, Math.min(y - dragStart.y, canvasSize.height - el.height))
						};
					} else if (isResizing) {
						const newElement = { ...el };

						switch (resizeHandle) {
						case "se":
							newElement.width = Math.max(20, x - el.x);
							newElement.height = Math.max(10, y - el.y);
							break;
						case "sw":
							newElement.width = Math.max(20, dragStart.elementWidth - (x - dragStart.elementX));
							newElement.height = Math.max(10, y - el.y);
							newElement.x = Math.min(x, dragStart.elementX + dragStart.elementWidth - 20);
							break;
						case "ne":
							newElement.width = Math.max(20, x - el.x);
							newElement.height = Math.max(10, dragStart.elementHeight - (y - dragStart.elementY));
							newElement.y = Math.min(y, dragStart.elementY + dragStart.elementHeight - 10);
							break;
						case "nw":
							newElement.width = Math.max(20, dragStart.elementWidth - (x - dragStart.elementX));
							newElement.height = Math.max(10, dragStart.elementHeight - (y - dragStart.elementY));
							newElement.x = Math.min(x, dragStart.elementX + dragStart.elementWidth - 20);
							newElement.y = Math.min(y, dragStart.elementY + dragStart.elementHeight - 10);
							break;
						default:
							break;
						}

						return newElement;
					}
				}
				return el;
			})
		);
	};

	const handleMouseUp = () => {
		setIsDragging(false);
		setIsResizing(false);
		setResizeHandle(null);
	};

	const updateElementProperty = (property, value) => {
		if (!selectedElement) return;

		setElements(prevElements =>
			prevElements.map(el =>
				el.id === selectedElement
					? { ...el, [property]: value }
					: el
			)
		);
	};

	const deleteElement = (id) => {
		setElements(prevElements => prevElements.filter(el => el.id !== id));
		setSelectedElement(null);
	};

	const handleZoomIn = () => {
		setZoom(prevZoom => Math.min(prevZoom + 0.25, 5));
	};

	const handleZoomOut = () => {
		setZoom(prevZoom => Math.max(prevZoom - 0.25, 0.5));
	};

	const handleZoomReset = () => {
		setZoom(3);
	};

	const exportToPDF = async (sizeName, doubleTag = false) => {
		const canvas = canvasRef.current;

		const htmlCanvas = await html2canvas(canvas, {
			scale: 3,
			useCORS: true,
			allowTaint: true,
			backgroundColor: "#ffffff"
		});

		const imgData = htmlCanvas.toDataURL("image/png");
		const widthMM = canvasSize.width * 0.352778;
		const heightMM = canvasSize.height * 0.352778;

		if (doubleTag) {
			// Para etiqueta dupla: etiqueta + espaço de 0,7cm + etiqueta novamente
			const spacing = 7; // 0,7cm em mm
			const totalHeight = heightMM + spacing + heightMM;

			const pdf = new jsPDF({
				orientation: widthMM > totalHeight ? "landscape" : "portrait",
				unit: "mm",
				format: [widthMM, totalHeight]
			});

			// Primeira etiqueta no topo
			pdf.addImage(imgData, "PNG", 0, 0, widthMM, heightMM);
			// Segunda etiqueta após o espaço de 0,7cm
			pdf.addImage(imgData, "PNG", 0, heightMM + spacing, widthMM, heightMM);
			pdf.save(`etiqueta-dupla-${sizeName}.pdf`);
		} else {
			// Etiqueta simples
			const pdf = new jsPDF({
				orientation: widthMM > heightMM ? "landscape" : "portrait",
				unit: "mm",
				format: [widthMM, heightMM]
			});

			pdf.addImage(imgData, "PNG", 0, 0, widthMM, heightMM);
			pdf.save(`etiqueta-${sizeName}.pdf`);
		}
	};

	const printPDF = async (doubleTag = false) => {
		const canvas = canvasRef.current;

		const htmlCanvas = await html2canvas(canvas, {
			scale: 3,
			useCORS: true,
			allowTaint: true,
			backgroundColor: "#ffffff"
		});

		const imgData = htmlCanvas.toDataURL("image/png");
		const widthMM = canvasSize.width * 0.352778;
		const heightMM = canvasSize.height * 0.352778;

		if (doubleTag) {
			// Para etiqueta dupla: etiqueta + espaço de 0,7cm + etiqueta novamente
			const spacing = 7; // 0,7cm em mm
			const totalHeight = heightMM + spacing + heightMM;

			const pdf = new jsPDF({
				orientation: widthMM > totalHeight ? "landscape" : "portrait",
				unit: "mm",
				format: [widthMM, totalHeight]
			});

			// Primeira etiqueta no topo
			pdf.addImage(imgData, "PNG", 0, 0, widthMM, heightMM);
			// Segunda etiqueta após o espaço de 0,7cm
			pdf.addImage(imgData, "PNG", 0, heightMM + spacing, widthMM, heightMM);
			pdf.autoPrint();
			window.open(pdf.output("bloburl"), "_blank");
		} else {
			// Etiqueta simples
			const pdf = new jsPDF({
				orientation: widthMM > heightMM ? "landscape" : "portrait",
				unit: "mm",
				format: [widthMM, heightMM]
			});

			pdf.addImage(imgData, "PNG", 0, 0, widthMM, heightMM);
			pdf.autoPrint();
			window.open(pdf.output("bloburl"), "_blank");
		}
	};

	const resetToInitialState = () => {
		if (initialElements.length > 0) {
			setElements(JSON.parse(JSON.stringify(initialElements)));
			setSelectedElement(null);
		}
	};

	return {
		canvasRef,
		elements,
		setElements,
		initialElements,
		setInitialElements,
		selectedElement,
		setSelectedElement,
		zoom,
		handleMouseDown,
		handleElementClick,
		handleMouseMove,
		handleMouseUp,
		updateElementProperty,
		deleteElement,
		handleZoomIn,
		handleZoomOut,
		handleZoomReset,
		exportToPDF,
		printPDF,
		resetToInitialState
	};
};
